// ============================================================================
// Section Config — Constants & Presets (M7)
// ============================================================================

import type { DirectionalSpacing, OverlayPreset } from '../types';

export const OVERLAY_PRESETS: Record<OverlayPreset, { label: string; gradient: string }> = {
  'dark-fade-up': {
    label: 'Dark Fade Up',
    gradient: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
  },
  'dark-fade-down': {
    label: 'Dark Fade Down',
    gradient: 'linear-gradient(to bottom, rgba(0,0,0,0.8), transparent)',
  },
  'dark-vignette': {
    label: 'Dark Vignette',
    gradient: 'radial-gradient(ellipse, transparent 40%, rgba(0,0,0,0.7))',
  },
  'brand-fade': {
    label: 'Brand Fade',
    gradient: 'linear-gradient(135deg, rgba(255,107,0,0.6), transparent)',
  },
  'manga-panel': {
    label: 'Manga Panel',
    gradient:
      'linear-gradient(to right, rgba(0,0,0,0.9) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.9) 100%)',
  },
};

export const CONTENT_MAX_WIDTH_PX: Record<string, string> = {
  narrow: '600px',
  standard: '900px',
  wide: '1200px',
  max: '1440px',
  full: '100%',
  // 'inherit' is NOT in this map — it's resolved at runtime from page config
};

export const CONTAINER_MAX_WIDTH_PX: Record<string, string> = {
  full: '100%',
  '1440px': '1440px',
  '1200px': '1200px',
  '960px': '960px',
};

export const PADDING_VALUES: Record<string, string> = {
  none: '0',
  compact: '24px 16px',
  standard: '48px 24px',
  spacious: '80px 32px',
  hero: '120px 48px',
};

// Spacing token → px for directional section padding
export const SECTION_SPACING_TOKEN_PX: Record<string, string> = {
  none: '0',
  xs: '8px',
  sm: '12px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  '2xl': '48px',
  '3xl': '64px',
  '4xl': '80px',
  '5xl': '96px',
  '6xl': '120px',
};

/** Maps legacy padding presets → spacing tokens for backward compat */
const LEGACY_PRESET_TO_TOKEN: Record<string, string> = {
  none: 'none',
  compact: 'sm',
  standard: 'md',
  spacious: 'xl',
  hero: '3xl',
};

/**
 * Resolve section padding — supports both preset strings and directional objects.
 */
export function resolveSectionPaddingCSS(
  padding: string | DirectionalSpacing | undefined,
): string {
  if (padding === undefined) return '0';
  if (typeof padding === 'string') {
    // Normalize legacy presets (standard/compact/spacious/hero) → spacing tokens
    // so the renderer matches the editor UI (LayoutSettings uses the same mapping).
    const normalized = LEGACY_PRESET_TO_TOKEN[padding] ?? padding;
    const tokenPx = SECTION_SPACING_TOKEN_PX[normalized];
    if (tokenPx !== undefined) return tokenPx;
    return '0';
  }
  const top = SECTION_SPACING_TOKEN_PX[padding.top ?? 'none'] ?? '0';
  const right = SECTION_SPACING_TOKEN_PX[padding.right ?? 'none'] ?? '0';
  const bottom = SECTION_SPACING_TOKEN_PX[padding.bottom ?? 'none'] ?? '0';
  const left = SECTION_SPACING_TOKEN_PX[padding.left ?? 'none'] ?? '0';
  return `${top} ${right} ${bottom} ${left}`;
}

export const ALIGN_X_CSS: Record<string, string> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

export const ALIGN_Y_CSS: Record<string, string> = {
  top: 'flex-start',
  center: 'center',
  bottom: 'flex-end',
};
